import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import JobSkip from "../../../../models/JobSkip";
import { dateMask, handleFocus } from "../../../../utils/masks";
import { TypeJob } from "../../../../models/enums/TypeJob";
import { getValidator } from "../../../../utils/validatorsYup";
import toastOptions from "../../../../utils/toastOptions";
import Modal from "../../../../components/modal";
import api from "../../../../services/api";
import moment from "../../../../utils/momentConfig";

interface Params {
  open: boolean;
  afterHandleSubmit: any;
  afterCloseModal: any;
}

export default function JobSkipAdd(props: Params) {
  const { open, afterHandleSubmit, afterCloseModal } = props;

  const [modalIsOpen, setModelOpen] = useState(false);
  const [job, setJob] = useState<JobSkip>();

  const schema = yup.object().shape({
    date: getValidator({ isDate: true }).required("Data obrigatória"),
    typeJob: yup.string().required('Tipo obrigatório'),
    description: yup.string().max(100, (val) => `Máximo ${val.max} caracteres`).min(5, (val) => `Mínimo ${val.min} caracteres`).required("Descrição obrigatória"),
  });

  const {
    register,
    handleSubmit,
    reset,
    errors,
    formState,
    setValue,
    clearErrors
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    setModelOpen(open);

    if (!open) return;

    setJob({
      date: moment().toDate()
    } as JobSkip);

  }, [open]);

  const closeModal = () => {
    handleCloseModal();
    reset();
    afterCloseModal();
  };

  const handleCloseModal = () => {
    clearErrors();
    setModelOpen(false);
    afterCloseModal();
  };

  const onSubmit = async (data: JobSkip) => {
    try {
      await api.post(`/jobs/skip`, data);
      toast.success("Configuração realizada com sucesso!", toastOptions);
      handleCloseModal();
      afterHandleSubmit();
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    }
  };

  return (
    <>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        className="modal-small"
        shouldCloseOnOverlayClick={false}
      >
        <div className="uk-modal-header">
          <span className="uk-modal-title uk-text-large">Configurar Job</span>
        </div>
        <button
          className="uk-modal-close-default"
          uk-close="true"
          type="button"
          onClick={handleCloseModal}
        />
        <form onReset={reset}
          onSubmit={e => {
            e.preventDefault();
            e.stopPropagation();
            return handleSubmit<JobSkip>(onSubmit)(e)
          }}
        >
          <div className="uk-modal-body">
            <div className="uk-grid-small" uk-grid="true">
              
              <div className="uk-width-1-6@s">
                <label className="uk-form-label" htmlFor="date">Data</label>
                <input type="text" 
                  id="date" 
                  name="date" 
                  placeholder="99/99/9999" 
                  className="uk-input uk-form-small" 
                  defaultValue={job?.date ? moment(job.date).format('DD/MM/YYYY') : ''}
                  autoComplete={"off"}
                  onFocus={handleFocus}
                  ref={register}
                  onChange={(e) => {
                    e.target.value = dateMask(e.target.value);
                    setJob({
                      ...job,
                      date: moment(e.target.value, 'DD/MM/YYYY').toDate()
                    } as JobSkip);
                    setValue('date', e.target.value);
                  }}
                />
                <div className="uk-flex uk-flex-column">
                  <small className="uk-text-danger">{errors.date?.message}</small>
                  <small className="uk-text-danger">{moment(job?.date).isSameOrAfter(moment(), 'day') ? null : <span className="uk-text-danger">Apenas data atual ou futura</span>}</small>
                  <small className="uk-text-muted uk-text-nowrap">{moment(job?.date).isValid() ? moment(job?.date).format('dddd') : ''  }</small>
                </div>
              </div>
              <div className="uk-width-1-2@s">
                <div className="uk-form-controls">
                  <label className="uk-form-label" htmlFor="typeJob">Tipo Job</label>
                  <div uk-form-custom="target: > * > span:first-child" className="uk-width-1">
                    <select className="uk-form-small"
                      id="typeJob"
                      name="typeJob"                      
                      defaultValue={job?.typeJob}
                      ref={register}
                      onChange={(e) => {
                        setJob({
                          ...job,
                          typeJob: e.target.value
                        } as JobSkip);
                        setValue('typeJob', e.target.value);
                      }}
                    >
                      {
                        TypeJob ? Object.keys(TypeJob).map((key) => {
                          return (
                            <option key={key} value={TypeJob[key as keyof typeof TypeJob]}>
                              {TypeJob[key as keyof typeof TypeJob]}
                            </option>
                          );
                        }) : null
                      }
                    </select>
                    <button
                      className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                      style={{textTransform: 'none'}} 
                      type="button" 
                      tabIndex={-1}
                    >
                      <span></span>
                      <span uk-icon="icon: chevron-down"></span>
                    </button>
                  </div>
                </div>
                <small className="uk-text-danger">{errors.typeJob?.message}</small>                
              </div>
              <div className="uk-width-1-1@s">
                <label className="uk-form-label" htmlFor="description">Descrição do Motivo (Pular pelo motivo)</label>
                <input
                  id="description"
                  name="description"
                  className="uk-input uk-form-small"
                  style={{ textTransform: "uppercase" }}
                  placeholder="Descrição do motivo"
                  defaultValue={job?.description}
                  autoComplete={"off"}
                  ref={register}
                />
                <small className="uk-text-danger">{errors.description?.message}</small>
              </div>
            </div>
          </div>
          <div className="uk-modal-footer">
            <div
              className="uk-grid-small uk-child-width-1-4@s uk-flex-center uk-text-center"
              uk-grid="true"
            >
              <button
                className="uk-button uk-button-default uk-button-small uk-modal-close uk-margin-left"
                onClick={handleCloseModal}
                type="button"
              >
                Cancelar
              </button>
              <button
                disabled={formState.isSubmitting}
                className="uk-button uk-button-primary uk-button-small uk-margin-left"
                type="submit"
              >
                {formState.isSubmitting && <div uk-spinner="true; ratio:0.5"></div>}
                Salvar
              </button>
            </div>
          </div>
        </form>
      </Modal>
    </>
  );
}
